import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Tooltip, Button, HStack, Icon, Text } from "@chakra-ui/react";
import { FiTrash, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { getSkillsList, deleteSkill } from "../../services/EvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";
import { epochConverterWithTimeZone } from "../../utils/dateUtils";
import { useUser } from "context/UserContext";
import ConfirmationModal from "../../common/modals/ConfirmationModal";
import { DarwinHeader, DarwinHeaderList } from "build-ts/common";


const SkillTable: React.FC = () => {
  const navigate = useNavigate();
  const hasMounted = useRef(false);
  const { defaultTimeZone } = useUser();

  const [skills, setSkills] = useState<DarwinHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  const [filters, setFilters] = useState<Record<string, string>>({ search: "" });
  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchSkills = useCallback(async (
    reset = false,
    currentSortBy = "created_date",
    currentSortOrder: "asc" | "desc" = "desc",
    currentFilters: Record<string, string> = filters,
    newOffset?: number
  ) => {
    try {
      if (reset) setLoading(true);
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: currentSortBy,
        sort_order: currentSortOrder,
      });

      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("search_string", currentFilters.search);
      }

      const result: DarwinHeaderList = DarwinHeaderList.fromJSON(await getSkillsList(params));

      if (result.headers) {
        setSkills(result.headers);
        if (result.total) {
          setPagination({
            total: result.total,
            has_more: (currentOffset + limit) < (result.total || 0),
            offset: currentOffset,
          });
        }
      } else {
        setSkills([]);
        setPagination({ total: 0, has_more: false, offset: currentOffset });
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      fetchSkills(true);
    }
  }, [fetchSkills]);

  const handlePageChange = useCallback(async (offset: number) => {
    await fetchSkills(false, sortBy, sortOrder, filters, offset);
  }, [fetchSkills, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchSkills(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchSkills, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    filtersRef.current = newFilters;
  }, []);

  const handleApplyFilters = useCallback(async () => {
    await fetchSkills(true, sortBy, sortOrder, filtersRef.current, 0);
  }, [fetchSkills, sortBy, sortOrder]);

  const handleClearFilters = useCallback(async () => {
    const cleared = { search: "" };
    setFilters(cleared);
    filtersRef.current = cleared;
    setSortBy("created_date");
    setSortOrder("desc");
    await fetchSkills(true, "created_date", "desc", cleared, 0);
  }, [fetchSkills]);

  const handleSelectionChange = useCallback((newSelected: Set<string>) => {
    setSelectedSkills(newSelected);
  }, []);

  const handleSkillRowClick = (skill: DarwinHeader) => {
    navigate(`/eva/skill/${skill.id}`);
  };

  const handleDeleteSelected = () => {
    if (selectedSkills.size !== 1) return;
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedSkills.size !== 1) return;
    try {
      setIsDeleting(true);
      const skillId = Array.from(selectedSkills)[0];
      await deleteSkill(skillId);
      setSelectedSkills(new Set());
      await fetchSkills(true, sortBy, sortOrder, filters, 0);
    } catch (error) {
      console.error("Error deleting skill:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const tableColumns: TableColumn<DarwinHeader>[] = useMemo(() => [
    {
      key: "name",
      label: "Name",
      sortable: true,
      className: "eva-name-column",
      render: (skill) => (
        <Tooltip label={skill.name} placement="top">
          <Text fontSize="sm" color="gray.700" noOfLines={2} wordBreak="break-word" mb={0}
            whiteSpace="normal" fontWeight="600">
            {skill.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "description",
      label: "Description",
      className: "eva-description-column",
      render: (skill) => (
        <Tooltip label={skill.description} placement="top">
          <Text fontSize="sm" color="gray.500" noOfLines={2} wordBreak="break-word" mb={0}>
            {skill.description || "—"}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "created_date",
      label: "Created At",
      sortable: true,
      className: "eva-date-column",
      render: (skill) => (
        <Text fontSize="xs" color="gray.700" mb={0}>
          {epochConverterWithTimeZone(skill.created_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    {
      key: "last_modified_date",
      label: "Last Modified",
      sortable: true,
      className: "eva-date-column",
      render: (skill) => (
        <Text fontSize="xs" color="gray.700" mb={0}>
          {epochConverterWithTimeZone(skill.last_modified_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const tableFilters: TableFilter[] = useMemo(() => [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search skills...",
      width: "300px",
    },
  ], []);

  const headerActions = selectedSkills.size > 0 ? (
    <HStack spacing={4}>
      {selectedSkills.size === 1 && (
        <Button
          onClick={handleDeleteSelected}
          colorScheme="red"
          size="sm"
          leftIcon={<Icon as={FiTrash} />}
        >
          Delete
        </Button>
      )}
      <Button
        onClick={() => setSelectedSkills(new Set())}
        variant="outline"
        size="sm"
        leftIcon={<Icon as={FiX} />}
      >
        Cancel
      </Button>
    </HStack>
  ) : null;

  return (
    <>
      <Table
        data={skills}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        limit={limit}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={tableFilters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        selectable={true}
        selectedItems={selectedSkills}
        onSelectionChange={handleSelectionChange}
        getItemId={(skill) => skill.id || ""}
        onRowClick={handleSkillRowClick}
        headerActions={headerActions}
        emptyMessage="No skills found"
        emptySubMessage="Try adjusting your search or create a new skill"
      />

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Skill"
        text={"Are you sure you want to delete this skill? This will also delete all its associated prompts. This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColorScheme="red"
        isLoading={isDeleting}
      />
    </>
  );
};

export default SkillTable;
